import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Fab,
  Stack
} from '@mui/material';
import { Add, Restaurant as RestaurantIcon, Visibility, Chat as ChatIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { restaurantApi, handleApiError } from '../services/api';
import type { Restaurant } from '../types';

const Dashboard = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRestaurant, setNewRestaurant] = useState({
    name: '',
    description: '',
    cuisine_type: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const data = await restaurantApi.getRestaurants();
      setRestaurants(data);
    } catch (err) {
      console.error('Failed to load restaurants:', err);
      const errorMessage = handleApiError(err);
      setError(`Failed to load restaurants: ${errorMessage}`);
      // Don't crash - just show empty state with error
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRestaurant = async () => {
    try {
      setCreating(true);
      setError(''); // Clear any previous errors
      const created = await restaurantApi.createRestaurant(newRestaurant);
      setRestaurants([...restaurants, created]);
      setCreateDialogOpen(false);
      setNewRestaurant({
        name: '',
        description: '',
        cuisine_type: ''
      });
    } catch (err) {
      console.error('Failed to create restaurant:', err);
      const errorMessage = handleApiError(err);
      setError(`Failed to create restaurant: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown date';
    }
  };

  // Always render something, even if loading or error
  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box mb={6}>
        <Typography variant="h1" gutterBottom>
          MNUbot - AI Menu Assistant
        </Typography>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Chat with AI about restaurant menus and get instant recommendations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a restaurant below to start chatting with our AI menu assistant
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress size={60} />
        </Box>
      ) : restaurants.length === 0 ? (
        <Box textAlign="center" py={8}>
          <RestaurantIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No restaurants yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first restaurant to get started with AI-powered menu assistance
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Restaurant
          </Button>
        </Box>
      ) : (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" component="h2">
              Restaurants ({restaurants.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add Restaurant
            </Button>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            }}
            gap={3}
          >
            {restaurants.map((restaurant) => (
              <Card
                key={restaurant.restaurant_id}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <RestaurantIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h3" noWrap>
                      {restaurant.name}
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={restaurant.cuisine_type}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  
                  {restaurant.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {restaurant.description}
                    </Typography>
                  )}
                  
                  <Stack spacing={1}>
                    {restaurant.metadata?.total_menu_items && (
                      <Typography variant="caption" color="text.secondary">
                        {restaurant.metadata.total_menu_items} menu items
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Created: {formatDate(restaurant.created_at)}
                    </Typography>
                  </Stack>
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/restaurant/${restaurant.restaurant_id}`)}
                    sx={{ flexGrow: 1 }}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ChatIcon />}
                    onClick={() => navigate(`/restaurant/${restaurant.restaurant_id}`)}
                    variant="outlined"
                  >
                    Chat
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Create Restaurant Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Restaurant</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Restaurant Name"
              value={newRestaurant.name}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Cuisine Type"
              value={newRestaurant.cuisine_type}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, cuisine_type: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Italian, Japanese, Mexican"
            />
            <TextField
              label="Description"
              value={newRestaurant.description}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Brief description of the restaurant..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateRestaurant}
            variant="contained"
            disabled={creating || !newRestaurant.name || !newRestaurant.cuisine_type}
            startIcon={creating ? <CircularProgress size={16} /> : <Add />}
          >
            {creating ? 'Creating...' : 'Create Restaurant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for quick access */}
      <Fab
        color="primary"
        aria-label="add restaurant"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000
        }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <Add />
      </Fab>
    </Container>
  );
};

export default Dashboard; 